import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import { toast } from '@theonefamily/ui';
import {
  Cpu,
  Wifi,
  WifiOff,
  AlertTriangle,
  Plus,
  Loader2,
  Activity,
  Link,
  Upload,
} from 'lucide-react';

interface IotDevice {
  id: string;
  device_serial: string;
  device_type: string;
  vehicle_id: string;
  vehicle_name: string;
  status: 'online' | 'offline' | 'error' | 'disconnected';
  last_heartbeat?: string;
  firmware_version?: string;
  dtc_codes?: { code: string; cleared_at?: string }[];
}

interface IotStats {
  total: number;
  online: number;
  offline: number;
  errors: number;
  dtcCount: number;
}

interface CreateDeviceInput {
  device_type: string;
  device_serial: string;
  vehicle_id: string;
  vehicle_name: string;
  firmware_version: string;
}

const IOT_DEVICE_TYPES = ['obd2', 'dashcam', 'trailer_sensor', 'temperature_sensor', 'fuel_sensor'];

const statusConfig: Record<string, { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  online: { label: 'Online', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Wifi },
  offline: { label: 'Offline', cls: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: WifiOff },
  error: { label: 'Error', cls: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  disconnected: { label: 'Disconnected', cls: 'bg-zinc-500/20 text-zinc-500 border-zinc-600/30', icon: WifiOff },
};

const typeLabels: Record<string, string> = {
  obd2: 'OBD-II',
  dashcam: 'Dashcam',
  trailer_sensor: 'Trailer Sensor',
  temperature_sensor: 'Temp Sensor',
  fuel_sensor: 'Fuel Sensor',
};

function timeAgo(iso?: string): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function IotDevicesPage() {
  const { data: devices, isLoading, refetch } = useApi<IotDevice[]>('/api/iot-devices');
  const { data: stats, refetch: refetchStats } = useApi<IotStats>('/api/iot-devices/stats');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignForm, setAssignForm] = useState({ vehicle_id: '', vehicle_name: '' });
  const [form, setForm] = useState<CreateDeviceInput>({
    device_type: 'obd2',
    device_serial: '',
    vehicle_id: '',
    vehicle_name: '',
    firmware_version: '',
  });

  const deviceList = devices ?? [];
  const s = stats ?? { total: 0, online: 0, offline: 0, errors: 0, dtcCount: 0 };

  const handleRegister = async () => {
    if (!form.device_serial || !form.vehicle_id || !form.vehicle_name) {
      setCreateError('Serial, Vehicle ID, and Vehicle Name are required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await apiFetch('/api/iot-devices', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setShowCreate(false);
      setForm({ device_type: 'obd2', device_serial: '', vehicle_id: '', vehicle_name: '', firmware_version: '' });
      refetch();
      refetchStats();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to register device');
    } finally {
      setCreating(false);
    }
  };

  const handleAssign = async (deviceId: string) => {
    if (!assignForm.vehicle_id || !assignForm.vehicle_name) return;
    try {
      await apiFetch(`/api/iot-devices/${deviceId}/assign`, {
        method: 'POST',
        body: JSON.stringify(assignForm),
      });
      setAssigningId(null);
      setAssignForm({ vehicle_id: '', vehicle_name: '' });
      refetch();
    } catch {
      // silently fail for now
    }
  };

  const handleOta = async (deviceId: string) => {
    try {
      await apiFetch(`/api/iot-devices/${deviceId}/ota`, { method: 'POST' });
    } catch (err) {
      // Expected 501 -- show alert
      toast.error(err instanceof Error ? err.message : 'OTA not available');
    }
  };

  const metricCards = [
    { label: 'Total Devices', value: s.total, icon: Cpu, color: 'text-blue-400' },
    { label: 'Online', value: s.online, icon: Wifi, color: 'text-emerald-400' },
    { label: 'Offline', value: s.offline, icon: WifiOff, color: 'text-zinc-400' },
    { label: 'Active DTCs', value: s.dtcCount, icon: AlertTriangle, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Activity className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">IoT Devices</h1>
            <p className="text-sm text-zinc-400">Vehicle telemetry and RMM-integrated devices</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Register Device
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((m) => (
          <div key={m.label} className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{m.label}</span>
              <m.icon className={`w-4 h-4 ${m.color}`} />
            </div>
            <span className="text-2xl font-bold text-zinc-100">{m.value}</span>
          </div>
        ))}
      </div>

      {/* Create Device Form */}
      {showCreate && (
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-zinc-200 mb-4">Register IoT Device</h2>
          {createError && (
            <div className="mb-4 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
              {createError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Device Type</label>
              <select
                value={form.device_type}
                onChange={e => setForm({ ...form, device_type: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:border-emerald-500"
              >
                {IOT_DEVICE_TYPES.map(t => (
                  <option key={t} value={t}>{typeLabels[t] ?? t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Serial Number</label>
              <input
                type="text"
                value={form.device_serial}
                onChange={e => setForm({ ...form, device_serial: e.target.value })}
                placeholder="e.g. OBD-00142"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Vehicle ID</label>
              <input
                type="text"
                value={form.vehicle_id}
                onChange={e => setForm({ ...form, vehicle_id: e.target.value })}
                placeholder="e.g. v-001"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Vehicle Name</label>
              <input
                type="text"
                value={form.vehicle_name}
                onChange={e => setForm({ ...form, vehicle_name: e.target.value })}
                placeholder="e.g. Truck-001"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Firmware Version</label>
              <input
                type="text"
                value={form.firmware_version}
                onChange={e => setForm({ ...form, firmware_version: e.target.value })}
                placeholder="e.g. 2.1.4"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleRegister}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Register
            </button>
            <button
              onClick={() => { setShowCreate(false); setCreateError(null); }}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Device Table */}
      <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
          </div>
        ) : deviceList.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Cpu className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No IoT devices registered</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Serial</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Type</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Vehicle</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Last Seen</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Firmware</th>
                  <th className="text-center text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">DTCs</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {deviceList.map((device) => {
                  const sc = statusConfig[device.status] ?? statusConfig['offline'] ?? { label: 'Unknown', cls: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: WifiOff };
                  const StatusIcon = sc.icon;
                  const activeDtcs = device.dtc_codes?.filter(d => !d.cleared_at).length ?? 0;
                  const isAssigning = assigningId === device.id;

                  return (
                    <tr key={device.id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono font-medium text-zinc-100">{device.device_serial}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          {typeLabels[device.device_type] ?? device.device_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isAssigning ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Vehicle ID"
                              value={assignForm.vehicle_id}
                              onChange={e => setAssignForm({ ...assignForm, vehicle_id: e.target.value })}
                              className="w-20 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-100 focus:outline-none focus:border-emerald-500"
                            />
                            <input
                              type="text"
                              placeholder="Name"
                              value={assignForm.vehicle_name}
                              onChange={e => setAssignForm({ ...assignForm, vehicle_name: e.target.value })}
                              className="w-24 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-100 focus:outline-none focus:border-emerald-500"
                            />
                            <button
                              onClick={() => handleAssign(device.id)}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => { setAssigningId(null); setAssignForm({ vehicle_id: '', vehicle_name: '' }); }}
                              className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs rounded transition-colors"
                            >
                              X
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-zinc-300">{device.vehicle_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${sc.cls}`}>
                          <StatusIcon className="w-3 h-3" />
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-zinc-400">{timeAgo(device.last_heartbeat)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-zinc-500">{device.firmware_version ?? '\u2014'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {activeDtcs > 0 ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold border border-amber-500/30">
                            {activeDtcs}
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setAssigningId(device.id); setAssignForm({ vehicle_id: device.vehicle_id, vehicle_name: device.vehicle_name }); }}
                            title="Assign to Vehicle"
                            className="p-1.5 rounded-md hover:bg-zinc-700/50 text-zinc-400 hover:text-emerald-400 transition-colors"
                          >
                            <Link className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleOta(device.id)}
                            title="Request OTA Update"
                            className="p-1.5 rounded-md hover:bg-zinc-700/50 text-zinc-400 hover:text-blue-400 transition-colors"
                          >
                            <Upload className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
